import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Truck, CreditCard, Check, MapPin, ChevronDown } from 'lucide-react';
import Header from './components/Header21';
import Footer from './components/Footer21';

const Checkout21 = () => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phone: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        pincode: '',
        country: 'India',
        cardNumber: '',
        cardName: '',
        expiryDate: '',
        cvv: '',
        paymentMethod: 'card',
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (currentStep < 3) {
            setCurrentStep(currentStep + 1);
        } else {
            navigate('/template21/orders');
        }
    };

    const steps = [
        { number: 1, title: 'Shipping', icon: Truck },
        { number: 2, title: 'Payment', icon: CreditCard },
        { number: 3, title: 'Review', icon: Check },
    ];

    const orderItems = [
        {
            name: 'Midnight Zari Lehenga',
            price: 48500,
            quantity: 1,
            size: 'M',
            image: 'https://images.pexels.com/photos/9419032/pexels-photo-9419032.jpeg?auto=compress&cs=tinysrgb&w=800',
        },
        {
            name: 'Noir Silk Saree',
            price: 32000,
            quantity: 1,
            size: 'Free Size',
            image: 'https://images.pexels.com/photos/11707056/pexels-photo-11707056.jpeg?auto=compress&cs=tinysrgb&w=800',
        },
        {
            name: 'Embroidered Anarkali Suite',
            price: 27500,
            quantity: 2,
            size: 'S',
            image: 'https://images.pexels.com/photos/6687182/pexels-photo-6687182.jpeg?auto=compress&cs=tinysrgb&w=800',
        },
    ];

    const indianStates = [
        'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
        'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
        'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
        'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
        'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
        'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
        'Delhi', 'Jammu & Kashmir', 'Ladakh', 'Puducherry',
        'Chandigarh', 'Andaman & Nicobar Islands', 'Dadra & Nagar Haveli',
        'Daman & Diu', 'Lakshadweep',
    ];

    const subtotal = orderItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const shipping = 0;
    const total = subtotal + shipping;

    const inputBaseClass = "w-full px-4 py-3 bg-[#1A1A1A] border border-[#2A2A2A] text-[#F5F0EB] text-sm rounded outline-none transition-colors duration-300 focus:border-[#C9A96E] placeholder-[#555]";
    const labelClass = "block text-[#F5F0EB] text-sm font-medium mb-2 tracking-wide";

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400;1,500&family=DM+Sans:wght@300;400;500;600;700&display=swap');
            `}</style>

            <div style={{ fontFamily: "'DM Sans', -apple-system, sans-serif" }} className="min-h-screen bg-[#0A0A0A]">
                <Header />

                <main className="pt-24 pb-16 px-4 lg:px-8">
                    <div className="max-w-[1400px] mx-auto">
                        {/* Page Header */}
                        <div className="mb-10">
                            <p className="text-[#C9A96E] text-sm tracking-[0.3em] uppercase mb-3">Secure Checkout</p>
                            <h1
                                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                                className="text-3xl lg:text-5xl font-light text-[#C9A96E] tracking-wide"
                            >
                                Checkout
                            </h1>
                        </div>

                        {/* Step Indicator */}
                        <div className="flex items-center justify-center mb-12 max-w-xl mx-auto">
                            {steps.map((step, index) => {
                                const Icon = step.icon;
                                const isActive = currentStep >= step.number;
                                const isCompleted = currentStep > step.number;

                                return (
                                    <React.Fragment key={step.number}>
                                        <div className="flex flex-col items-center relative">
                                            <div
                                                className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all duration-500 ${
                                                    isActive
                                                        ? 'bg-[#C9A96E] border-[#C9A96E] text-[#0A0A0A]'
                                                        : 'border-[#2A2A2A] text-[#8A8A7A]'
                                                }`}
                                            >
                                                {isCompleted ? <Check size={20} /> : <Icon size={20} />}
                                            </div>
                                            <span
                                                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                                                className={`mt-2 text-sm font-medium tracking-wide ${
                                                    isActive ? 'text-[#C9A96E]' : 'text-[#8A8A7A]'
                                                }`}
                                            >
                                                {step.title}
                                            </span>
                                        </div>
                                        {index < steps.length - 1 && (
                                            <div
                                                className={`flex-1 h-px mx-4 mt-[-20px] transition-colors duration-500 ${
                                                    currentStep > step.number ? 'bg-[#C9A96E]' : 'bg-[#2A2A2A]'
                                                }`}
                                            />
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
                            {/* Form Section */}
                            <div className="lg:col-span-2">
                                <form onSubmit={handleSubmit}>
                                    {/* Step 1: Shipping */}
                                    {currentStep === 1 && (
                                        <div className="bg-[#141414] border border-[#2A2A2A] rounded-lg p-6 lg:p-8">
                                            <div className="flex items-center gap-3 mb-8">
                                                <MapPin size={22} className="text-[#C9A96E]" />
                                                <h2
                                                    style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                                                    className="text-2xl font-medium text-[#F5F0EB] tracking-wide"
                                                >
                                                    Shipping Information
                                                </h2>
                                            </div>

                                            <div className="space-y-5">
                                                <div>
                                                    <label className={labelClass}>Full Name *</label>
                                                    <input
                                                        type="text"
                                                        name="fullName"
                                                        value={formData.fullName}
                                                        onChange={handleInputChange}
                                                        required
                                                        placeholder="Enter your full name"
                                                        className={inputBaseClass}
                                                    />
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                                    <div>
                                                        <label className={labelClass}>Email *</label>
                                                        <input
                                                            type="email"
                                                            name="email"
                                                            value={formData.email}
                                                            onChange={handleInputChange}
                                                            required
                                                            placeholder="you@example.com"
                                                            className={inputBaseClass}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className={labelClass}>Phone *</label>
                                                        <input
                                                            type="tel"
                                                            name="phone"
                                                            value={formData.phone}
                                                            onChange={handleInputChange}
                                                            required
                                                            placeholder="+91 98765 43210"
                                                            className={inputBaseClass}
                                                        />
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className={labelClass}>Address Line 1 *</label>
                                                    <input
                                                        type="text"
                                                        name="addressLine1"
                                                        value={formData.addressLine1}
                                                        onChange={handleInputChange}
                                                        required
                                                        placeholder="House/Flat No., Street name"
                                                        className={inputBaseClass}
                                                    />
                                                </div>

                                                <div>
                                                    <label className={labelClass}>Address Line 2</label>
                                                    <input
                                                        type="text"
                                                        name="addressLine2"
                                                        value={formData.addressLine2}
                                                        onChange={handleInputChange}
                                                        placeholder="Apartment, landmark (optional)"
                                                        className={inputBaseClass}
                                                    />
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                                                    <div>
                                                        <label className={labelClass}>City *</label>
                                                        <input
                                                            type="text"
                                                            name="city"
                                                            value={formData.city}
                                                            onChange={handleInputChange}
                                                            required
                                                            placeholder="City"
                                                            className={inputBaseClass}
                                                        />
                                                    </div>
                                                    <div className="relative">
                                                        <label className={labelClass}>State *</label>
                                                        <div className="relative">
                                                            <select
                                                                name="state"
                                                                value={formData.state}
                                                                onChange={handleInputChange}
                                                                required
                                                                className={`${inputBaseClass} appearance-none pr-10`}
                                                            >
                                                                <option value="" disabled>Select state</option>
                                                                {indianStates.map(state => (
                                                                    <option key={state} value={state} style={{ backgroundColor: '#1A1A1A', color: '#F5F0EB' }}>
                                                                        {state}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A8A7A] pointer-events-none" />
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label className={labelClass}>Pincode *</label>
                                                        <input
                                                            type="text"
                                                            name="pincode"
                                                            value={formData.pincode}
                                                            onChange={handleInputChange}
                                                            required
                                                            placeholder="6-digit PIN"
                                                            maxLength="6"
                                                            pattern="[0-9]{6}"
                                                            className={inputBaseClass}
                                                        />
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className={labelClass}>Country</label>
                                                    <input
                                                        type="text"
                                                        name="country"
                                                        value={formData.country}
                                                        onChange={handleInputChange}
                                                        className={inputBaseClass}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Step 2: Payment */}
                                    {currentStep === 2 && (
                                        <div className="bg-[#141414] border border-[#2A2A2A] rounded-lg p-6 lg:p-8">
                                            <div className="flex items-center gap-3 mb-8">
                                                <CreditCard size={22} className="text-[#C9A96E]" />
                                                <h2
                                                    style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                                                    className="text-2xl font-medium text-[#F5F0EB] tracking-wide"
                                                >
                                                    Payment Details
                                                </h2>
                                            </div>

                                            {/* Payment Method Tabs */}
                                            <div className="flex gap-3 mb-8">
                                                {[
                                                    { id: 'card', label: 'Card' },
                                                    { id: 'upi', label: 'UPI' },
                                                    { id: 'netbanking', label: 'Net Banking' },
                                                ].map(method => (
                                                    <button
                                                        key={method.id}
                                                        type="button"
                                                        onClick={() => setFormData(prev => ({ ...prev, paymentMethod: method.id }))}
                                                        className={`px-5 py-2.5 text-sm font-medium rounded border transition-all duration-300 ${
                                                            formData.paymentMethod === method.id
                                                                ? 'bg-[#C9A96E] border-[#C9A96E] text-[#0A0A0A]'
                                                                : 'border-[#2A2A2A] text-[#8A8A7A] hover:border-[#C9A96E] hover:text-[#C9A96E]'
                                                        }`}
                                                    >
                                                        {method.label}
                                                    </button>
                                                ))}
                                            </div>

                                            {formData.paymentMethod === 'card' && (
                                                <div className="space-y-5">
                                                    <div>
                                                        <label className={labelClass}>Card Number *</label>
                                                        <input
                                                            type="text"
                                                            name="cardNumber"
                                                            value={formData.cardNumber}
                                                            onChange={handleInputChange}
                                                            required
                                                            placeholder="1234 5678 9012 3456"
                                                            maxLength="19"
                                                            className={inputBaseClass}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className={labelClass}>Cardholder Name *</label>
                                                        <input
                                                            type="text"
                                                            name="cardName"
                                                            value={formData.cardName}
                                                            onChange={handleInputChange}
                                                            required
                                                            placeholder="Name on card"
                                                            className={inputBaseClass}
                                                        />
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-5">
                                                        <div>
                                                            <label className={labelClass}>Expiry Date *</label>
                                                            <input
                                                                type="text"
                                                                name="expiryDate"
                                                                value={formData.expiryDate}
                                                                onChange={handleInputChange}
                                                                required
                                                                placeholder="MM/YY"
                                                                maxLength="5"
                                                                className={inputBaseClass}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className={labelClass}>CVV *</label>
                                                            <input
                                                                type="password"
                                                                name="cvv"
                                                                value={formData.cvv}
                                                                onChange={handleInputChange}
                                                                required
                                                                placeholder="***"
                                                                maxLength="4"
                                                                className={inputBaseClass}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {formData.paymentMethod === 'upi' && (
                                                <div>
                                                    <label className={labelClass}>UPI ID *</label>
                                                    <input
                                                        type="text"
                                                        name="upiId"
                                                        placeholder="yourname@upi"
                                                        className={inputBaseClass}
                                                    />
                                                </div>
                                            )}

                                            {formData.paymentMethod === 'netbanking' && (
                                                <div>
                                                    <label className={labelClass}>Select Bank *</label>
                                                    <div className="relative">
                                                        <select className={`${inputBaseClass} appearance-none pr-10`}>
                                                            <option value="" disabled selected>Choose your bank</option>
                                                            <option value="sbi" style={{ backgroundColor: '#1A1A1A', color: '#F5F0EB' }}>State Bank of India</option>
                                                            <option value="hdfc" style={{ backgroundColor: '#1A1A1A', color: '#F5F0EB' }}>HDFC Bank</option>
                                                            <option value="icici" style={{ backgroundColor: '#1A1A1A', color: '#F5F0EB' }}>ICICI Bank</option>
                                                            <option value="axis" style={{ backgroundColor: '#1A1A1A', color: '#F5F0EB' }}>Axis Bank</option>
                                                            <option value="kotak" style={{ backgroundColor: '#1A1A1A', color: '#F5F0EB' }}>Kotak Mahindra Bank</option>
                                                        </select>
                                                        <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A8A7A] pointer-events-none" />
                                                    </div>
                                                </div>
                                            )}

                                            <div className="flex items-center gap-2 mt-6 pt-6 border-t border-[#2A2A2A]">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C9A96E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                                </svg>
                                                <span className="text-[#8A8A7A] text-xs">
                                                    Your payment information is encrypted and secure
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    {/* Step 3: Review */}
                                    {currentStep === 3 && (
                                        <div className="bg-[#141414] border border-[#2A2A2A] rounded-lg p-6 lg:p-8">
                                            <h2
                                                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                                                className="text-2xl font-medium text-[#F5F0EB] mb-8 tracking-wide"
                                            >
                                                Review Your Order
                                            </h2>

                                            <div className="space-y-8">
                                                {/* Shipping Summary */}
                                                <div className="pb-6 border-b border-[#2A2A2A]">
                                                    <div className="flex items-center justify-between mb-3">
                                                        <h3 className="text-[#C9A96E] text-sm font-medium tracking-wider uppercase">
                                                            Shipping Address
                                                        </h3>
                                                        <button
                                                            type="button"
                                                            onClick={() => setCurrentStep(1)}
                                                            className="text-[#C9A96E] text-xs hover:text-[#D4B87A] transition-colors underline"
                                                        >
                                                            Edit
                                                        </button>
                                                    </div>
                                                    <div className="text-[#8A8A7A] text-sm leading-relaxed">
                                                        <p className="text-[#F5F0EB]">{formData.fullName || 'Your Name'}</p>
                                                        <p>{formData.addressLine1 || 'Address Line 1'}</p>
                                                        {formData.addressLine2 && <p>{formData.addressLine2}</p>}
                                                        <p>{formData.city || 'City'}, {formData.state || 'State'} {formData.pincode || 'Pincode'}</p>
                                                        <p>{formData.country}</p>
                                                        <p className="mt-1">{formData.phone || 'Phone'} &middot; {formData.email || 'Email'}</p>
                                                    </div>
                                                </div>

                                                {/* Payment Summary */}
                                                <div className="pb-6 border-b border-[#2A2A2A]">
                                                    <div className="flex items-center justify-between mb-3">
                                                        <h3 className="text-[#C9A96E] text-sm font-medium tracking-wider uppercase">
                                                            Payment Method
                                                        </h3>
                                                        <button
                                                            type="button"
                                                            onClick={() => setCurrentStep(2)}
                                                            className="text-[#C9A96E] text-xs hover:text-[#D4B87A] transition-colors underline"
                                                        >
                                                            Edit
                                                        </button>
                                                    </div>
                                                    <p className="text-[#8A8A7A] text-sm">
                                                        {formData.paymentMethod === 'card'
                                                            ? `Card ending in ${formData.cardNumber ? formData.cardNumber.slice(-4) : '****'}`
                                                            : formData.paymentMethod === 'upi'
                                                                ? 'UPI Payment'
                                                                : 'Net Banking'
                                                        }
                                                    </p>
                                                </div>

                                                {/* Items Summary */}
                                                <div>
                                                    <h3 className="text-[#C9A96E] text-sm font-medium tracking-wider uppercase mb-4">
                                                        Order Items
                                                    </h3>
                                                    <div className="space-y-4">
                                                        {orderItems.map((item, index) => (
                                                            <div key={index} className="flex items-center gap-4">
                                                                <div className="w-14 h-16 flex-shrink-0 overflow-hidden rounded">
                                                                    <img
                                                                        src={item.image}
                                                                        alt={item.name}
                                                                        className="w-full h-full object-cover"
                                                                    />
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="text-[#F5F0EB] text-sm truncate">{item.name}</p>
                                                                    <p className="text-[#8A8A7A] text-xs">
                                                                        Size: {item.size} &middot; Qty: {item.quantity}
                                                                    </p>
                                                                </div>
                                                                <span className="text-[#C9A96E] text-sm font-medium">
                                                                    {'\u20B9'}{(item.price * item.quantity).toLocaleString('en-IN')}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Navigation Buttons */}
                                    <div className="flex gap-4 mt-6">
                                        {currentStep > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => setCurrentStep(currentStep - 1)}
                                                className="px-8 py-4 border border-[#2A2A2A] text-[#8A8A7A] text-sm font-medium tracking-wider uppercase hover:border-[#C9A96E] hover:text-[#C9A96E] transition-all duration-300"
                                            >
                                                Back
                                            </button>
                                        )}
                                        <button
                                            type="submit"
                                            className="flex-1 px-8 py-4 bg-[#C9A96E] text-[#0A0A0A] text-sm font-semibold tracking-wider uppercase hover:bg-[#D4B87A] transition-colors duration-300"
                                        >
                                            {currentStep === 3 ? 'Place Order' : currentStep === 1 ? 'Continue to Payment' : 'Review Order'}
                                        </button>
                                    </div>
                                </form>
                            </div>

                            {/* Order Summary Sidebar */}
                            <div className="lg:col-span-1">
                                <div className="bg-[#141414] border border-[#2A2A2A] rounded-lg p-6 lg:sticky lg:top-24">
                                    <h2
                                        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                                        className="text-xl font-medium text-[#F5F0EB] mb-6 tracking-wide"
                                    >
                                        Order Summary
                                    </h2>

                                    {/* Items */}
                                    <div className="space-y-4 mb-6 pb-6 border-b border-[#2A2A2A]">
                                        {orderItems.map((item, index) => (
                                            <div key={index} className="flex items-center gap-3">
                                                <div className="w-12 h-14 flex-shrink-0 overflow-hidden rounded">
                                                    <img
                                                        src={item.image}
                                                        alt={item.name}
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-[#F5F0EB] text-xs truncate">{item.name}</p>
                                                    <p className="text-[#8A8A7A] text-xs">Qty: {item.quantity}</p>
                                                </div>
                                                <span className="text-[#C9A96E] text-xs font-medium whitespace-nowrap">
                                                    {'\u20B9'}{(item.price * item.quantity).toLocaleString('en-IN')}
                                                </span>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Price Breakdown */}
                                    <div className="space-y-3 mb-6 pb-6 border-b border-[#2A2A2A]">
                                        <div className="flex justify-between">
                                            <span className="text-[#8A8A7A] text-sm">Subtotal</span>
                                            <span className="text-[#F5F0EB] text-sm">{'\u20B9'}{subtotal.toLocaleString('en-IN')}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-[#8A8A7A] text-sm">Shipping</span>
                                            <span className="text-[#2ECC71] text-sm">Complimentary</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-[#8A8A7A] text-sm">Tax</span>
                                            <span className="text-[#8A8A7A] text-sm">Included</span>
                                        </div>
                                    </div>

                                    {/* Total */}
                                    <div className="flex justify-between items-center">
                                        <span
                                            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                                            className="text-lg font-medium text-[#F5F0EB]"
                                        >
                                            Total
                                        </span>
                                        <span
                                            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                                            className="text-2xl font-semibold text-[#C9A96E]"
                                        >
                                            {'\u20B9'}{total.toLocaleString('en-IN')}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </main>

                <Footer />
            </div>
        </>
    );
};

export default Checkout21;
